/**
 * Reset Password — navy+gold AI Search Portal landing.
 *
 * KOPS-511: rewrite of the legacy CardResetPassword page (which used
 * the AuthCardLayout "KairosIQ AI Neural Network" cyan branding) into
 * a self-contained navy+gold surface that matches the rest of the
 * AISearchPortal domain. Landing page for the reset link in the
 * KOPS-511 navy+gold reset email.
 *
 * Mounted at /agent-portal/reset-password inside the AISearchPortal
 * domain branch of Routes.tsx. The legacy card page is preserved for
 * the main Kairos branch.
 *
 * Backend unchanged: POST /api/auth/reset-password?token=... with
 * { password, confirm_password }.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../helpers/api';
import PasswordStrengthMeter, { evaluatePassword } from '../components/auth/PasswordStrengthMeter';

const colors = {
  navy:  '#0F1E35',
  navy2: '#162844',
  navy3: '#1E3557',
  gold:  '#C9952A',
  gold2: '#E8B44A',
  gold3: '#F5D080',
  white: '#FFFFFF',
  off:   '#E8EEF6',
  muted: '#8FA3BF',
  green: '#27ae60',
  red:   '#e74c3c',
};

const fonts = {
  display: "'Bebas Neue', Impact, 'Arial Black', sans-serif",
  body:    "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif",
};

const FONT_LINK =
  'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';

const ResetPasswordAIForward: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ kind: 'ok' | 'err'; msg: string } | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setResult({ kind: 'err', msg: 'Missing reset token. Please use the link from your email.' });
      return;
    }
    if (!password || !confirmPassword) {
      setResult({ kind: 'err', msg: 'Please fill in both fields.' });
      return;
    }
    if (password !== confirmPassword) {
      setResult({ kind: 'err', msg: 'Passwords do not match.' });
      return;
    }
    // KOPS-511 — mirror the backend policy. The server re-validates
    // so this is defense in depth + instant feedback, not the only
    // gate.
    const policy = evaluatePassword(password);
    if (!policy.valid) {
      setResult({
        kind: 'err',
        msg: 'Password does not meet the security policy. See the checklist below.',
      });
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const res = await axiosInstance.post(
        `/auth/reset-password?token=${token}`,
        { password, confirm_password: confirmPassword }
      );
      setResult({
        kind: 'ok',
        msg: res?.data?.message || 'Password updated. Redirecting to sign in…',
      });
      setPassword('');
      setConfirmPassword('');
      // Brief pause so the user sees the success banner before bouncing.
      setTimeout(() => navigate('/agent-portal/login'), 1500);
    } catch (err: any) {
      setResult({
        kind: 'err',
        msg:
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Reset failed. The link may be expired — please request a new one.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToLogin = () => navigate('/agent-portal/login');

  return (
    <div
      style={{
        minHeight: '100vh',
        background: colors.navy,
        fontFamily: fonts.body,
        color: colors.off,
        padding: '40px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <link href={FONT_LINK} rel="stylesheet" />

      <div
        style={{
          width: '100%',
          maxWidth: '520px',
          background: colors.navy2,
          border: `1px solid ${colors.navy3}`,
          borderTop: `4px solid ${colors.gold}`,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
        }}
      >
        {/* Brand header */}
        <div
          style={{
            textAlign: 'center',
            padding: '40px 32px 24px',
            borderBottom: `1px solid ${colors.navy3}`,
          }}
        >
          <div
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              letterSpacing: '3px',
              color: colors.white,
              marginBottom: '6px',
            }}
          >
            AI<span style={{ color: colors.gold2 }}>Search</span>Portal
          </div>
          <div
            style={{
              color: colors.muted,
              fontSize: '11px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              marginBottom: '28px',
            }}
          >
            AI-Powered Real Estate Compliance Platform
          </div>

          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px auto',
              fontSize: '32px',
              color: colors.navy,
              fontWeight: 900,
              boxShadow: '0 8px 24px rgba(201, 149, 42, 0.35)',
            }}
          >
            🔐
          </div>

          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              letterSpacing: '1.5px',
              color: colors.white,
              margin: '0 0 8px',
              lineHeight: 1.15,
            }}
          >
            Set a New Password
          </h1>
          <p
            style={{
              fontSize: '13px',
              color: colors.muted,
              margin: 0,
              lineHeight: 1.5,
            }}
          >
            Choose a strong password you haven't used before.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
          <label
            htmlFor="password"
            style={{
              display: 'block',
              fontSize: '10px',
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 700,
              marginBottom: '8px',
            }}
          >
            New Password
          </label>
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 12 characters"
            disabled={submitting}
            autoComplete="new-password"
            style={{
              width: '100%',
              background: colors.navy3,
              color: colors.white,
              border: `1px solid ${colors.navy3}`,
              borderRadius: '8px',
              padding: '14px 16px',
              fontSize: '14px',
              fontFamily: fonts.body,
              outline: 'none',
              marginBottom: '12px',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
            onBlur={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
          />

          {/* KOPS-511 — live strength meter + rules checklist. Shows
              four-segment bar, strength label, and a ✓/○ list of
              which requirements the password meets. */}
          <PasswordStrengthMeter password={password} />

          <label
            htmlFor="confirm-password"
            style={{
              display: 'block',
              fontSize: '10px',
              color: colors.muted,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              fontWeight: 700,
              marginBottom: '8px',
            }}
          >
            Confirm Password
          </label>
          <input
            id="confirm-password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Retype your new password"
            disabled={submitting}
            autoComplete="new-password"
            style={{
              width: '100%',
              background: colors.navy3,
              color: colors.white,
              border: `1px solid ${colors.navy3}`,
              borderRadius: '8px',
              padding: '14px 16px',
              fontSize: '14px',
              fontFamily: fonts.body,
              outline: 'none',
              marginBottom: '18px',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = colors.gold)}
            onBlur={(e) => (e.currentTarget.style.borderColor = colors.navy3)}
          />

          {result && (
            <div
              style={{
                background:
                  result.kind === 'ok' ? `${colors.green}22` : `${colors.red}22`,
                border: `1px solid ${
                  result.kind === 'ok' ? colors.green : colors.red
                }`,
                borderLeft: `3px solid ${
                  result.kind === 'ok' ? colors.green : colors.red
                }`,
                color: result.kind === 'ok' ? colors.green : colors.red,
                padding: '12px 14px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 600,
                lineHeight: 1.5,
                marginBottom: '18px',
              }}
            >
              {result.kind === 'ok' ? '✅ ' : '⚠️ '}
              {result.msg}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              background: submitting
                ? colors.muted
                : `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 50%, ${colors.gold3} 100%)`,
              color: colors.navy,
              border: 'none',
              borderRadius: '8px',
              padding: '14px 20px',
              fontSize: '13px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1.2px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: fonts.body,
              boxShadow: submitting
                ? 'none'
                : '0 8px 24px rgba(201, 149, 42, 0.35)',
            }}
          >
            {submitting ? 'Saving new password…' : 'Set New Password →'}
          </button>

          <button
            type="button"
            onClick={handleBackToLogin}
            disabled={submitting}
            style={{
              width: '100%',
              background: 'transparent',
              color: colors.gold2,
              border: 'none',
              padding: '14px 0 0 0',
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontFamily: fonts.body,
            }}
          >
            ← Back to Sign In
          </button>
        </form>

        <div
          style={{
            background: colors.navy,
            padding: '20px 32px',
            textAlign: 'center',
            borderTop: `1px solid ${colors.navy3}`,
          }}
        >
          <div
            style={{
              fontSize: '11px',
              color: colors.muted,
              lineHeight: 1.6,
              marginBottom: '6px',
            }}
          >
            🔒 Secure, compliant, and built for real estate professionals
          </div>
          <div style={{ fontSize: '10px', color: colors.muted }}>
            © 2026 AI Search Portal. All rights reserved.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordAIForward;
